import moment from 'moment'
import JsonViewer from 'vue-json-viewer'
import {
  getReportTypes,
  getReportTypeLabel,
  getReportSummary,
  requestReports,
  findSourceRecords,
  countSourceRecords,
  findAggregateRecords,
  countAggregateRecords,
  findCustomRecords,
  countCustomRecords,
  archiveSourceRecord,
  archiveAggregateRecord,
  archiveCustomRecord
} from '../../../../services/reports-service'
import { C_ADD_SITE_ALERT } from '../../../../utilities/mutation-types'
import { addAlert } from '../../../../utilities/helpers'

export default {
  name: 'advanced-reports',
  components: {
    JsonViewer
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      loadingSummary: false,
      loadingRecords: false,
      summaryPollHandle: null,
      reportTypes: [],
      summary: {
        source: { pending: 0, processing: 0, completed: 0, failed: 0, stale: 0, total: 0 },
        aggregate: { pending: 0, processing: 0, completed: 0, failed: 0, stale: 0, total: 0 },
        custom: { pending: 0, processing: 0, completed: 0, failed: 0, stale: 0, total: 0 }
      },
      requestForm: {
        reportType: null,
        target: 'source',
        reportFidelity: 'day',
        category: null,
        categoryId: null,
        dateStart: moment.utc().subtract(1, 'day').format('YYYY-MM-DD'),
        dateEnd: moment.utc().subtract(1, 'day').format('YYYY-MM-DD'),
        recalculate: false
      },
      requestResultMessage: null,
      requestDialog: false,
      activeRecordType: 'source',
      deletingRecordId: null,
      dataDialog: false,
      selectedRecord: null,
      records: [],
      totalRecords: 0,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 25,
        sortBy: 'dateUpdated'
      },
      filters: {
        reportType: null,
        reportStatus: null,
        categoryId: null,
        reportFidelity: null,
        dateFrom: null,
        dateTo: null
      },
      statusOptions: ['pending', 'processing', 'completed', 'failed', 'stale']
    }
  },
  computed: {
    reportTypeOptions () {
      return (this.reportTypes || []).map((item) => ({
        label: `${getReportTypeLabel(item.reportType)}${item.nightly ? ' (nightly)' : ' (on-demand)'}`,
        value: item.reportType
      }))
    },
    targetOptions () {
      return ['source', 'custom']
    },
    fidelityOptions () {
      return ['day', 'week', 'month', 'quarter', 'year', 'custom']
    },
    canSubmitReportRequest () {
      return !!(this.requestForm &&
        this.requestForm.reportType &&
        this.requestForm.dateStart &&
        this.requestForm.dateEnd &&
        !this.apiInProgress)
    },
    summaryCards () {
      const summary = this.summary || {}
      const source = summary.source || {}
      const aggregate = summary.aggregate || {}
      const custom = summary.custom || {}
      return [
        { key: 'pending', label: 'Pending', source: source.pending || 0, aggregate: aggregate.pending || 0, custom: custom.pending || 0 },
        { key: 'processing', label: 'Processing', source: source.processing || 0, aggregate: aggregate.processing || 0, custom: custom.processing || 0 },
        { key: 'completed', label: 'Completed', source: source.completed || 0, aggregate: aggregate.completed || 0, custom: custom.completed || 0 },
        { key: 'failed', label: 'Failed', source: source.failed || 0, aggregate: aggregate.failed || 0, custom: custom.failed || 0 },
        { key: 'stale', label: 'Stale', source: source.stale || 0, aggregate: aggregate.stale || 0, custom: custom.stale || 0 },
        { key: 'total', label: 'Total', source: source.total || 0, aggregate: aggregate.total || 0, custom: custom.total || 0 }
      ]
    },
    tableHeaders () {
      return [
        { text: 'Report Type', value: 'reportType' },
        { text: 'Fidelity', value: 'reportFidelity' },
        { text: 'Status', value: 'reportStatus' },
        { text: 'Period Start', value: 'periodStart' },
        { text: 'Period End', value: 'periodEnd' },
        { text: 'Category', value: 'category' },
        { text: 'Category ID', value: 'categoryId' },
        { text: 'Date Updated', value: 'dateUpdated' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  async mounted () {
    await this.loadReportTypes()
    await this.loadSummary()
    await this.loadRecords()
    this.startSummaryPolling()
  },
  beforeDestroy () {
    this.stopSummaryPolling()
  },
  methods: {
    openDataDialog (record) {
      if (!record) return
      this.selectedRecord = record
      this.dataDialog = true
    },
    formatRecordTitle (record) {
      if (!record) return ''
      const parts = [
        record.reportType || 'unknown',
        '(' + (record.reportFidelity || 'day') + ')',
        record.category || '',
        record.categoryId ? record.categoryId.substring(0, 8) + '...' : ''
      ].filter(Boolean)
      return parts.join(' ')
    },
    formatTimestamp (value) {
      if (!value) return '-'
      return moment.utc(value).format('YYYY-MM-DD HH:mm:ss')
    },
    async loadReportTypes () {
      this.apiInProgress = true
      try {
        const data = await getReportTypes()
        this.reportTypes = Array.isArray(data) ? data : []
      } catch (err) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'error', message: err.message || 'Failed to load report types' })
      } finally {
        this.apiInProgress = false
      }
    },
    async loadSummary () {
      this.loadingSummary = true
      try {
        const dateStart = this.resolveFilterDateStart()
        const dateEnd = this.resolveFilterDateEnd()
        const summary = await getReportSummary({
          reportType: this.filters.reportType || undefined,
          categoryId: this.filters.categoryId || undefined,
          dateStart: dateStart || undefined,
          dateEnd: dateEnd || undefined
        })
        if (summary) {
          this.summary = summary
        }
      } catch (err) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'error', message: err.message || 'Failed to load report summary' })
      } finally {
        this.loadingSummary = false
      }
    },
    startSummaryPolling () {
      this.stopSummaryPolling()
      this.summaryPollHandle = setTimeout(async () => {
        await this.loadSummary()
        this.startSummaryPolling()
      }, 10000)
    },
    stopSummaryPolling () {
      clearTimeout(this.summaryPollHandle)
      this.summaryPollHandle = null
    },
    onReportTypeChange (reportType) {
      const selected = (this.reportTypes || []).find((item) => item.reportType === reportType)
      if (selected) {
        this.requestForm.target = selected.target || 'source'
        this.requestForm.category = selected.categories && selected.categories.length ? selected.categories[0].category : null
      }
    },
    async submitReportRequest () {
      if (!this.requestForm.reportType) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'warning', message: 'Select a report type' })
        return
      }
      if (!this.requestForm.dateStart || !this.requestForm.dateEnd) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'warning', message: 'Select date start and date end' })
        return
      }

      const selectedDateStart = moment.utc(this.requestForm.dateStart, 'YYYY-MM-DD', true)
      const selectedDateEnd = moment.utc(this.requestForm.dateEnd, 'YYYY-MM-DD', true)

      if (!selectedDateStart.isValid() || !selectedDateEnd.isValid()) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'warning', message: 'Select valid date start and date end' })
        return
      }

      const requestDateStart = `${selectedDateStart.format('YYYY-MM-DD')}T00:00:00.000Z`
      const requestDateEnd = `${selectedDateEnd.format('YYYY-MM-DD')}T00:00:00.000Z`

      this.apiInProgress = true
      this.requestResultMessage = null
      try {
        const result = await requestReports({
          reportType: this.requestForm.reportType,
          target: this.requestForm.target || 'source',
          reportFidelity: this.requestForm.reportFidelity || 'day',
          category: this.requestForm.category || undefined,
          categoryId: this.requestForm.categoryId || undefined,
          dateStart: requestDateStart,
          dateEnd: requestDateEnd,
          recalculate: !!this.requestForm.recalculate
        })

        this.requestResultMessage = result && result.scheduledJobId
          ? `Report request queued (Job ID: ${result.scheduledJobId})`
          : 'Report request queued successfully'

        this.requestDialog = false
        await this.loadSummary()
        await this.loadRecords()
      } catch (err) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'error', message: err.message || 'Failed to request reports' })
      } finally {
        this.apiInProgress = false
      }
    },
    buildWhereFilter () {
      const where = { and: [{ dateArchived: null }] }
      const dateStart = this.resolveFilterDateStart()
      const dateEnd = this.resolveFilterDateEnd()

      if (this.filters.reportType) {
        where.and.push({ reportType: this.filters.reportType })
      }
      if (this.filters.reportStatus) {
        where.and.push({ reportStatus: this.filters.reportStatus })
      }
      if (this.filters.categoryId) {
        where.and.push({ categoryId: this.filters.categoryId })
      }
      if (this.activeRecordType === 'aggregate' && this.filters.reportFidelity) {
        where.and.push({ reportFidelity: this.filters.reportFidelity })
      }
      if (dateStart) {
        where.and.push({ periodStart: { gte: dateStart } })
      }
      if (dateEnd) {
        where.and.push({ periodEnd: { lte: dateEnd } })
      }

      return where
    },
    async loadRecords () {
      this.loadingRecords = true
      try {
        const page = this.pagination.page || 1
        const limit = this.pagination.rowsPerPage || 25
        const offset = (page - 1) * limit
        const sortBy = this.pagination.sortBy || 'dateUpdated'
        const sortDirection = this.pagination.descending ? 'DESC' : 'ASC'
        const order = `${sortBy} ${sortDirection}`
        const where = this.buildWhereFilter()
        const filter = { where, limit, offset, order }

        let recordsPromise = null
        let countPromise = null
        if (this.activeRecordType === 'aggregate') {
          recordsPromise = findAggregateRecords(filter)
          countPromise = countAggregateRecords(where)
        } else if (this.activeRecordType === 'custom') {
          recordsPromise = findCustomRecords(filter)
          countPromise = countCustomRecords(where)
        } else {
          recordsPromise = findSourceRecords(filter)
          countPromise = countSourceRecords(where)
        }

        const [records, countResult] = await Promise.all([recordsPromise, countPromise])
        this.records = records || []
        this.totalRecords = countResult && countResult.count ? Number(countResult.count) : 0
      } catch (err) {
        this.$store.commit(C_ADD_SITE_ALERT, { type: 'error', message: err.message || 'Failed to load report records' })
      } finally {
        this.loadingRecords = false
      }
    },
    async onRecordTypeChange () {
      if (this.activeRecordType !== 'aggregate' && this.filters.reportFidelity) {
        this.filters.reportFidelity = null
      }
      this.pagination.page = 1
      await this.loadRecords()
    },
    async onFilterChange () {
      this.pagination.page = 1
      await this.loadSummary()
      await this.loadRecords()
    },
    async onPagination () {
      await this.loadRecords()
    },
    isDeletingRecord (record) {
      if (!record || record.id === null || record.id === undefined || this.deletingRecordId === null) {
        return false
      }
      return String(this.deletingRecordId) === String(record.id)
    },
    async deleteReportRecord (record) {
      if (!record || record.id === null || record.id === undefined || this.deletingRecordId) {
        return
      }

      const recordLabel = [
        record.reportType || 'unknown',
        '(' + (record.reportFidelity || 'day') + ')',
        this.formattedDateForConfirm(record.periodStart) + ' to ' + this.formattedDateForConfirm(record.periodEnd)
      ].join(' ')

      if (!confirm('Confirm deleting report record: ' + recordLabel + '?')) {
        return
      }

      const recordId = String(record.id)
      this.deletingRecordId = recordId
      let deleteSucceeded = false
      try {
        if (this.activeRecordType === 'aggregate') {
          await archiveAggregateRecord(recordId)
        } else if (this.activeRecordType === 'custom') {
          await archiveCustomRecord(recordId)
        } else {
          await archiveSourceRecord(recordId)
        }

        deleteSucceeded = true
        addAlert({ message: 'Report record deleted', type: 'success' })
      } catch (err) {
        addAlert({ message: err.message || 'Failed to delete report record', type: 'error' })
      } finally {
        this.deletingRecordId = null
      }

      if (deleteSucceeded) {
        await this.loadSummary()
        await this.loadRecords()
      }
    },
    formattedDateForConfirm (value) {
      if (!value) return '-'
      return moment.utc(value).format('YYYY-MM-DD')
    },
    resolveFilterDateStart () {
      if (!this.filters.dateFrom) {
        return null
      }

      const start = moment.utc(this.filters.dateFrom, 'YYYY-MM-DD', true)
      if (!start.isValid()) {
        return null
      }

      return start.startOf('day').toISOString()
    },
    resolveFilterDateEnd () {
      if (!this.filters.dateTo) {
        return null
      }

      const end = moment.utc(this.filters.dateTo, 'YYYY-MM-DD', true)
      if (!end.isValid()) {
        return null
      }

      return end.endOf('day').toISOString()
    }
  }
}
